import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Button,
  Tooltip,
  Alert,
  Snackbar,
  Menu,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
} from "react-icons/fa";
import { GrDocumentPpt } from "react-icons/gr";
import { MdOutlineFileDownload, MdOutlineFeedback } from "react-icons/md";
import { RiEditLine, RiShareForwardLine } from "react-icons/ri";
const BASE_URL = process.env.REACT_APP_BASE_URL;
const PresentationViewDialog = ({
  open,
  onClose,
  slides = [],
  pptUrl = "",
  pptId,
  initialVersion = null,
  versionsList = null,
  publicationId = null,
  token = null,
  setAlert,
  onStartRegenerate,
  onDownload,
  onEdit,
  professionalType = null,
}) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [currentPptUrl, setCurrentPptUrl] = useState(pptUrl);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [alertInfo, setAlertInfo] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [mlrStatus, setMlrStatus] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState("");
  const [feedbackConsent, setFeedbackConsent] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [previousFeedbacks, setPreviousFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [internalVersionsList, setInternalVersionsList] = useState(null);
  const authToken = token || localStorage.getItem("authToken");

  // Get organization data to check if MLR downloads should be disabled
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");
  const disableMlrDownload = organizationData?.id === 4;

  // Fetch MLR status and poll every 3 seconds while dialog is open (stop when ready)
  useEffect(() => {
    if (!open || !pptId) {
      return;
    }

    let targetProjectId = pptId;
    const activeVersionsList = versionsList || internalVersionsList;
    if (activeVersionsList && activeVersionsList.length > 0 && selectedVersion) {
      const vObj = activeVersionsList.find(
        (v) => (v.display_version || v.version_number) === selectedVersion
      );
      if (vObj && vObj.project_id) {
        targetProjectId = vObj.project_id;
      }
    }

    const fetchMlrStatus = async () => {
      try {
        const response = await fetch(`${BASE_URL}/mlr-status?pptId=${targetProjectId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const isReady = data.ppt_mlr === true || data.ppt_mlr === "True";
          setMlrStatus(isReady);
          // Stop polling if MLR is ready
          if (isReady && intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (error) {
        console.error("Error fetching MLR status:", error);
      }
    };

    // Only start polling if MLR is not already ready
    let intervalId;
    fetchMlrStatus();
    if (!mlrStatus) {
      intervalId = setInterval(fetchMlrStatus, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [open, pptId, authToken, mlrStatus, selectedVersion, versionsList, internalVersionsList]);

  useEffect(() => {
    if (open) {
      fetchVersions();
    } else {
      setAvailableVersions([]);
      setSelectedVersion(null);
      setInternalVersionsList(null);
    }
  }, [open, pptId]);


  const fetchVersions = async () => {
    setVersionsLoading(true);
    setCurrentPptUrl("");
    try {
      if (versionsList && versionsList.length > 0) {
        // UI dropdown options (display versions)
        const versions = versionsList.map((v, index) => Number(v.display_version || (index + 1)));
        setAvailableVersions(versions);

        // initialVersion passed from parent is the display version
        const targetVersion = initialVersion && versions.includes(Number(initialVersion)) ? Number(initialVersion) : Math.max(...versions);
        setSelectedVersion(targetVersion);

        const targetIndex = versions.indexOf(targetVersion);
        const targetObj = targetIndex >= 0 ? versionsList[targetIndex] : versionsList[versionsList.length - 1];
        const targetProjectId = targetObj ? targetObj.project_id : pptId;
        const targetDbVersion = targetObj ? targetObj.version_number : targetVersion;

        setCurrentPptUrl(`${BASE_URL}/ppt/${targetProjectId}?versionNumber=${targetDbVersion}&t=${Date.now()}`);
        setVersionsLoading(false);
        return;
      }

      // If no versionsList was provided (e.g. from main Presentation card), fetch full history
      if (publicationId && authToken) {
        const response = await fetch(`${BASE_URL}/publication/${publicationId}/versions`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          let targetGroup = null;
          for (const vGroup of (data.versions || [])) {
            if (vGroup.type === 'ppt' && vGroup.versions) {
              const ownsId = vGroup.versions.some(v => Number(v.project_id) === Number(pptId)) ||
                (vGroup.project_id && vGroup.project_id.some(id => Number(id) === Number(pptId)));
              if (ownsId) {
                targetGroup = vGroup;
                break;
              }
            }
          }

          if (targetGroup && targetGroup.versions && targetGroup.versions.length > 0) {
            const allVersions = targetGroup.versions;
            setInternalVersionsList(allVersions);

            const versions = allVersions.map((v, index) => Number(v.display_version || (index + 1)));
            setAvailableVersions(versions);

            // if we have a stored version, find it. Otherwise pick the newest
            const targetVersion = initialVersion && versions.includes(Number(initialVersion)) ? Number(initialVersion) : Math.max(...versions);
            setSelectedVersion(targetVersion);

            const targetIndex = versions.indexOf(targetVersion);
            const targetObj = targetIndex >= 0 ? allVersions[targetIndex] : allVersions[allVersions.length - 1];
            const targetProjectId = targetObj ? targetObj.project_id : pptId;
            const targetDbVersion = targetObj ? targetObj.version_number : targetVersion;

            setCurrentPptUrl(`${BASE_URL}/ppt/${targetProjectId}?versionNumber=${targetDbVersion}&t=${Date.now()}`);
            setVersionsLoading(false);
            return;
          }
        }
      }

      const response = await fetch(
        `${BASE_URL}/ppt_project_current_version?pptProjectId=${pptId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const totalVersions = data.ppt_current_version?.total_versions || 0;

        if (totalVersions > 0) {
          const versions = Array.from({ length: totalVersions }, (_, i) => i + 1);
          setAvailableVersions(versions);
          const targetVersion = initialVersion && versions.includes(Number(initialVersion)) ? Number(initialVersion) : totalVersions;
          setSelectedVersion(targetVersion);
          setCurrentPptUrl(`${BASE_URL}/ppt/${pptId}?versionNumber=${targetVersion}&t=${Date.now()}`);
        } else {
          setCurrentPptUrl(pptUrl || "");
        }
      } else {
        setCurrentPptUrl(pptUrl || "");
      }
    } catch (error) {
      console.error("Error fetching versions:", error);
      setCurrentPptUrl(pptUrl || "");
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleChangeVersion = async (event) => {
    // This value is the UI 'display_version'
    const displayVersionNumber = Number(event.target.value);
    setSelectedVersion(displayVersionNumber);

    let targetProjectId = pptId;
    let targetDbVersion = displayVersionNumber;

    const activeVersionsList = versionsList || internalVersionsList;
    if (activeVersionsList && activeVersionsList.length > 0) {
      const versions = activeVersionsList.map((v, index) => Number(v.display_version || (index + 1)));
      const targetIndex = versions.indexOf(displayVersionNumber);
      const vObj = targetIndex >= 0 ? activeVersionsList[targetIndex] : null;
      if (vObj && vObj.project_id) {
        targetProjectId = vObj.project_id;
        targetDbVersion = vObj.version_number;
      }
    }
    setCurrentPptUrl(`${BASE_URL}/ppt/${targetProjectId}?versionNumber=${targetDbVersion}&t=${Date.now()}`);
  };

  const handleComingSoon = () => {
    setAlertInfo({ severity: "info", message: "Coming soon" });
  };

  const handleCloseAlert = () => {
    setAlertInfo(null);
  };

  const handleEdit = () => {
    if (onEdit) {
      let targetProjectId = pptId;
      const activeVersionsList = versionsList || internalVersionsList;
      if (activeVersionsList && activeVersionsList.length > 0 && selectedVersion) {
        const vObj = activeVersionsList.find(
          (v) => (v.display_version || v.version_number) === selectedVersion
        );
        if (vObj && vObj.project_id) {
          targetProjectId = vObj.project_id;
        }
      }
      onEdit(targetProjectId);
    } else {
      handleComingSoon();
    }
  };

  const handleShare = async () => {
    if (!pptId) {
      setAlertInfo({ severity: "error", message: "PPT not available for sharing" });
      return;
    }
    setSharingInProgress(true);
    setShareDialogOpen(true);
    setShareUrl("");
    try {
      const getResponse = await fetch(`${BASE_URL}/ppt/${pptId}/share`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.share_url) {
          setShareUrl(getData.share_url);
          setSharingInProgress(false);
          return;
        }
      }
      const postResponse = await fetch(`${BASE_URL}/ppt/${pptId}/share`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pptId: pptId }),
      });
      if (postResponse.ok) {
        const postData = await postResponse.json();
        setShareUrl(postData.share_url);
      } else {
        console.error("[PresentationViewDialog] Share failed:", postResponse.status);
        setAlertInfo({ severity: "error", message: "Failed to create share link" });
        setShareDialogOpen(false);
      }
    } catch (error) {
      console.error("[PresentationViewDialog] Error creating share link:", error);
      setAlertInfo({ severity: "error", message: "Error creating share link" });
      setShareDialogOpen(false);
    } finally {
      setSharingInProgress(false);
    }
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setAlertInfo({ severity: "success", message: "Link copied to clipboard!" });
    } catch (error) {
      console.error("[PresentationViewDialog] Error copying to clipboard:", error);
      setAlertInfo({ severity: "error", message: "Failed to copy link" });
    }
  };

  const handleOpenFeedback = () => {
    setFeedbackRating(0);
    setFeedbackComments("");
    setFeedbackConsent(false);
    setFeedbackDialogOpen(true);
    fetchPreviousFeedbacks();
  };

  const fetchPreviousFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const url = `${BASE_URL}/project_feedbacks?id=${pptId}&type=presentation&version=${selectedVersion || 1}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPreviousFeedbacks(data.feedbacks || []);
      } else {
        console.error("Failed to fetch previous feedbacks:", response.status);
        setPreviousFeedbacks([]);
      }
    } catch (error) {
      console.error("Error fetching previous feedbacks:", error);
      setPreviousFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      setAlertInfo({ severity: "warning", message: "Please select a rating" });
      return;
    }
    setSubmittingFeedback(true);
    try {
      const formData = new FormData();
      formData.append("stars", feedbackRating);
      formData.append("comments", feedbackComments);
      formData.append("id", pptId);
      formData.append("type", "ppt");
      formData.append("version", selectedVersion || 1);
      const response = await fetch(`${BASE_URL}/submit_feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });
      if (response.ok) {
        setAlertInfo({ severity: "success", message: "Feedback submitted successfully!" });
        setFeedbackDialogOpen(false);
        setFeedbackRating(0);
        setFeedbackComments("");
        setFeedbackConsent(false);
      } else {
        console.error("[PresentationViewDialog] Feedback submission failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to submit feedback" });
      }
    } catch (error) {
      console.error("[PresentationViewDialog] Error submitting feedback:", error);
      setAlertInfo({ severity: "error", message: "Error submitting feedback" });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ratingOptions = [
    { value: 1, label: "Awful", emoji: "😣" },
    { value: 2, label: "Poor", emoji: "😞" },
    { value: 3, label: "Neutral", emoji: "😐" },
    { value: 4, label: "Good", emoji: "🤩" },
    { value: 5, label: "Excellent", emoji: "😍" },
  ];

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownload = async (downloadPpt = true, downloadPptMlr = false) => {
    handleDownloadClose();
    if (!pptId || !selectedVersion) {
      if (setAlert) {
        setAlert({ severity: "error", message: "Unable to download. Please try again." });
      }
      return;
    }
    setAlertInfo({ severity: "info", message: "Downloading, please wait..." });
    try {
      let targetProjectId = pptId;
      let targetDbVersion = selectedVersion;

      const activeVersionsList = versionsList || internalVersionsList;
      if (activeVersionsList && activeVersionsList.length > 0) {
        const vObj = activeVersionsList.find(
          (v) => (v.display_version || v.version_number) === selectedVersion
        );
        if (vObj) {
          if (vObj.project_id) targetProjectId = vObj.project_id;
          if (vObj.version_number) targetDbVersion = vObj.version_number;
        }
      }

      const downloadUrl = `${BASE_URL}/ppt/download/${targetProjectId}?version_id=${targetDbVersion}&download_ppt=${downloadPpt}&download_ppt_mlr=${downloadPptMlr}`;
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = downloadPptMlr
          ? `Pubvision_presentation_MLR_v${selectedVersion}.zip`
          : `Pubvision_presentation_v${selectedVersion}.pptx`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);
        setAlertInfo({ severity: "success", message: "Downloaded successfully!" });
        setTimeout(() => {
          setAlertInfo(null);
        }, 3000);
      } else {
        setAlertInfo({ severity: "error", message: "Failed to download" });
      }
    } catch (error) {
      console.error("Error downloading PPT:", error);
      setAlertInfo({ severity: "error", message: "Failed to download" });
    }
  };

  const handleNextSlide = () => {
    if (!slides || slides.length === 0) return;
    setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };

  const handlePrevSlide = () => {
    if (!slides || slides.length === 0) return;
    setCurrentSlideIndex(
      (prevIndex) => (prevIndex - 1 + slides.length) % slides.length
    );
  };

  // ✅ Helper: render the slide viewer area
  const renderSlideViewer = () => {
    // While fetching versions, show a centered spinner instead of a broken iframe
    if (versionsLoading) {
      return (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#111",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </div>
      );
    }

    if (slides && slides.length > 0) {
      return (
        <>
          <img
            src={slides[currentSlideIndex]}
            alt={`Slide ${currentSlideIndex + 1}`}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              // ✅ Force high-quality downscaling — prevents blurry resampling
              imageRendering: "auto",
              WebkitFontSmoothing: "antialiased",
            }}
            // ✅ Decode eagerly so the image paints at full res on first render
            decoding="sync"
            loading="eager"
          />
          <IconButton
            onClick={handlePrevSlide}
            style={{
              position: "absolute",
              top: "50%",
              left: "20px",
              transform: "translateY(-50%)",
              color: "#fff",
            }}
          >
            <FaArrowLeft />
          </IconButton>
          <IconButton
            onClick={handleNextSlide}
            style={{
              position: "absolute",
              top: "50%",
              right: "20px",
              transform: "translateY(-50%)",
              color: "#fff",
            }}
          >
            <FaArrowRight />
          </IconButton>
        </>
      );
    }

    if (currentPptUrl) {
      // ✅ Render iframe at 2× dimensions then scale down via CSS transform.
      // This forces Office Online to rasterize at double resolution, eliminating
      // the blurriness caused by its default low-DPI rendering.
      const SCALE = 2;
      return (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <iframe
            key={currentPptUrl}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              currentPptUrl
            )}`}
            style={{
              border: "none",
              // Render at 2× logical size
              width: `${100 * SCALE}%`,
              height: `${100 * SCALE}%`,
              // Scale back down to fit — origin top-left keeps it anchored correctly
              transform: `scale(${1 / SCALE})`,
              transformOrigin: "top left",
            }}
            title="PowerPoint Viewer"
          />
        </div>
      );
    }

    return (
      <p
        style={{
          color: "#fff",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        No PPT available
      </p>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: "1000px",
            maxWidth: "1000px",
            borderRadius: "8px",
            overflow: "visible",
            fontFamily: "'Inter', sans-serif",
          },
        }}
        sx={{
          "& *": { fontFamily: "'Inter', sans-serif !important" },
        }}
      >
        <IconButton
          style={{ position: "absolute", top: 8, right: 8, color: "#838383" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle style={{ padding: "16px", paddingRight: "56px", borderBottom: "1px solid #eee" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <GrDocumentPpt style={{ color: "#1E4DE3", fontSize: "20px" }} />
            </div>
            <Select
              size="small"
              value={selectedVersion || ""}
              onChange={handleChangeVersion}
              sx={{
                height: 36,
                fontSize: "14px",
                color: "#1E4DE3",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d3d3d3",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1E4DE3",
                },
                "&:hover": {
                  backgroundColor: "#e8f0fe",
                  boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1E4DE3",
                },
              }}
              disabled={availableVersions.length === 0}
            >
              {availableVersions.map((versionNum) => (
                <MenuItem key={versionNum} value={versionNum}>
                  v{versionNum}
                </MenuItem>
              ))}
            </Select>
            {professionalType && (
              <Chip
                label={`Audience: ${professionalType}`}
                variant="outlined"
                sx={{
                  borderColor: "#1E4DE3",
                  color: "#1E4DE3",
                  fontWeight: 600,
                  fontSize: "13px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#e8f0fe",
                  px: 1,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
              />
            )}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              {onEdit ? (
                <Button
                  variant="outlined"
                  startIcon={<RiEditLine style={{ color: "#1E4DE3" }} />}
                  onClick={handleEdit}
                  sx={{
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                    textTransform: "none",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                      borderColor: "#1E4DE3",
                      boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                      backgroundColor: "#e8f0fe",
                    },
                  }}
                >
                  Edit
                </Button>
              ) : (
                <Tooltip title="Coming soon">
                  <Button
                    variant="contained"
                    startIcon={<RiEditLine style={{ color: "#000" }} />}
                    onClick={handleEdit}
                    style={{
                      backgroundColor: "#d3d3d3",
                      color: "#000",
                      textTransform: "none",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    }}
                  >
                    Edit
                  </Button>
                </Tooltip>
              )}
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <Button
                  variant="outlined"
                  startIcon={<MdOutlineFileDownload style={{ color: "#1E4DE3" }} />}
                  onClick={() => handleDownload(true, false)}
                  sx={{
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                    textTransform: "none",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    "&:hover": {
                      borderColor: "#1E4DE3",
                      boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                      backgroundColor: "#e8f0fe",
                    },
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownloadClick}
                  sx={{
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                    minWidth: "36px",
                    padding: "6px 8px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "none",
                    "&:hover": {
                      borderColor: "#1E4DE3",
                      borderLeft: "none",
                      boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                      backgroundColor: "#e8f0fe",
                    },
                  }}
                >
                  <FaChevronDown style={{ fontSize: "12px" }} />
                </Button>
                <Menu
                  anchorEl={downloadAnchorEl}
                  open={Boolean(downloadAnchorEl)}
                  onClose={handleDownloadClose}
                  PaperProps={{
                    style: {
                      fontFamily: "'Inter', sans-serif",
                    },
                  }}
                  MenuListProps={{
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "14px",
                        color: "#333",
                        "&:hover": {
                          backgroundColor: "#e8f0fe",
                          color: "#1e4de3",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem onClick={() => handleDownload(true, false)}>
                    Download PPT
                  </MenuItem>
                  {!disableMlrDownload && (
                    mlrStatus ? (
                      <MenuItem onClick={() => handleDownload(false, true)}>
                        Download PPT MLR
                      </MenuItem>
                    ) : (
                      <Tooltip
                        title="MLR report is in progress, please try after sometime"
                        arrow
                      >
                        <MenuItem style={{ color: "#999", cursor: "not-allowed" }}>
                          Download PPT MLR
                        </MenuItem>
                      </Tooltip>
                    )
                  )}
                </Menu>
              </div>
              <Button
                variant="outlined"
                startIcon={
                  sharingInProgress ? (
                    <CircularProgress size={16} sx={{ color: "#1E4DE3" }} />
                  ) : (
                    <RiShareForwardLine style={{ color: "#1E4DE3" }} />
                  )
                }
                onClick={handleShare}
                disabled={sharingInProgress}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                  },
                }}
              >
                Share
              </Button>
              <Button
                variant="outlined"
                startIcon={<MdOutlineFeedback style={{ color: "#1E4DE3" }} />}
                onClick={handleOpenFeedback}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Feedback
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "16px" }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              backgroundColor: "#000",
              marginBottom: "16px",
            }}
          >
            {/* ✅ Replaced inline IIFE with extracted renderSlideViewer() */}
            {renderSlideViewer()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "8px",
              minWidth: 400,
              backgroundColor: "#2d2d2d",
              color: "#fff",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            color: "#fff",
          }}
        >
          <span style={{ fontSize: "16px" }}>
            Link to 'Pubvision Presentation' copied
          </span>
          <IconButton
            onClick={() => setShareDialogOpen(false)}
            sx={{ color: "#fff", padding: "4px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 16px 16px 16px" }}>
          {sharingInProgress ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#fff" }} />
              <span style={{ marginLeft: "12px", color: "#fff" }}>
                Loading share link...
              </span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#3d3d3d",
                borderRadius: "4px",
                padding: "8px 12px",
              }}
            >
              <input
                type="text"
                value={shareUrl}
                readOnly
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "14px",
                  outline: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              <Button
                variant="contained"
                onClick={handleCopyShareUrl}
                startIcon={<ContentCopyIcon />}
                sx={{
                  backgroundColor: "#4a4a4a",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#5a5a5a",
                  },
                }}
              >
                Copy
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "16px",
              minWidth: 400,
              maxWidth: 450,
              maxHeight: "90vh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: "0 0 16px 0",
            fontWeight: 600,
            fontSize: "18px",
          }}
        >
          Help us improve our product for you
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflowY: "auto" }}>
          {loadingFeedbacks ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              {/* Previous Reviews Section */}
              {previousFeedbacks.length > 0 && (
                <Box sx={{ marginBottom: "24px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                  >
                    Previous Reviews
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px",
                    }}
                  >
                    {previousFeedbacks.map((feedback, index) => {
                      const matchedOption = ratingOptions.find(
                        (opt) => opt.value === feedback.stars
                      ) || { emoji: "❓", label: "Unknown" };
                      return (
                        <Box
                          key={index}
                          sx={{
                            padding: "12px",
                            marginBottom: "8px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "6px",
                            "&:last-child": { marginBottom: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <span style={{ fontSize: "24px", marginRight: "8px" }}>
                              {matchedOption.emoji}
                            </span>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#333" }}
                            >
                              {matchedOption.label}
                            </Typography>
                          </Box>
                          {feedback.comments && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#555", marginBottom: "4px" }}
                            >
                              {feedback.comments}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            {new Date(feedback.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Add New Review Section */}
              {previousFeedbacks.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                >
                  Add New Review
                </Typography>
              )}

              <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
                {previousFeedbacks.length > 0
                  ? "Share your latest thoughts with us:"
                  : "How would you like to describe your experience with the app today?"}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                {ratingOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => setFeedbackRating(option.value)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor:
                        feedbackRating === option.value ? "#e8f0fe" : "transparent",
                      border:
                        feedbackRating === option.value
                          ? "2px solid #1E4DE3"
                          : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: "32px" }}>{option.emoji}</span>
                    <span style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
              <TextField
                multiline
                rows={4}
                placeholder="Tell us more about your experience (optional)"
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value.slice(0, 500))}
                fullWidth
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                }}
              />
              <p style={{ margin: "0 0 16px 0", color: "#999", fontSize: "12px" }}>
                {500 - feedbackComments.length}/500 characters left
              </p>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={feedbackConsent}
                    onChange={(e) => setFeedbackConsent(e.target.checked)}
                    sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                  />
                }
                label={
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    I consent to PubVision AI contacting me based on my feedback
                  </span>
                }
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "20px",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setFeedbackDialogOpen(false)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#ccc",
                    color: "#333",
                    "&:hover": {
                      borderColor: "#999",
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#1E4DE3",
                    "&:hover": {
                      backgroundColor: "#1a3fb8",
                    },
                  }}
                >
                  {submittingFeedback ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Submit feedback"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for alerts */}
      <Snackbar
        open={!!alertInfo}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertInfo?.severity || "info"}
          sx={{ width: "100%" }}
        >
          {alertInfo?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

PresentationViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slides: PropTypes.array,
  pptUrl: PropTypes.string,
  pptId: PropTypes.number,
  setAlert: PropTypes.func,
  onStartRegenerate: PropTypes.func,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  professionalType: PropTypes.string,
};

export default PresentationViewDialog;
